/**
 * TanStack Query hooks and key factory for vendor registry operations.
 */

import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query"
import { vendorRegistryApiService, type CreateVendorRequest } from "@/services/vendorRegistryApi"

import { invalidateMembership } from "./usePrincipals"

export const vendorKeys = {
  all: ["vendors"] as const,
  lists: () => [...vendorKeys.all, "list"] as const,
  list: () => [...vendorKeys.lists()] as const,
  details: () => [...vendorKeys.all, "detail"] as const,
  detail: (vendorId: string | undefined) => [...vendorKeys.details(), vendorId ?? null] as const,
  users: (vendorId: string | undefined) => [...vendorKeys.detail(vendorId), "users"] as const,
}

/**
 * Fetch every vendor organization.
 */
export function useVendorList() {
  return useQuery({
    queryKey: vendorKeys.list(),
    queryFn: () => vendorRegistryApiService.listVendors(),
  })
}

/**
 * Fetch one vendor organization. Disabled until vendorId is set.
 */
export function useVendorDetail(vendorId: string | undefined) {
  return useQuery({
    queryKey: vendorKeys.detail(vendorId),
    queryFn: () => {
      if (!vendorId) throw new Error("vendorId is required")
      return vendorRegistryApiService.getVendor(vendorId)
    },
    enabled: !!vendorId,
  })
}

/**
 * Fetch every member of a vendor company. Disabled until vendorId is set.
 */
export function useVendorUsers(vendorId: string | undefined) {
  return useQuery({
    queryKey: vendorKeys.users(vendorId),
    queryFn: () => {
      if (!vendorId) throw new Error("vendorId is required")
      return vendorRegistryApiService.listVendorUsers(vendorId)
    },
    enabled: !!vendorId,
  })
}

/**
 * Create a vendor and invalidate the vendor list.
 */
export function useCreateVendor() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (request: CreateVendorRequest) => vendorRegistryApiService.createVendor(request),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: vendorKeys.lists() })
    },
  })
}

/**
 * Add a person to a vendor company by email and refresh every membership list.
 */
export function useAddVendorUser(vendorId: string) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (email: string) => vendorRegistryApiService.addVendorUser(vendorId, email),
    onSuccess: () => invalidateMembership(queryClient),
  })
}

/**
 * Remove a person from a vendor company and refresh every membership list.
 */
export function useRemoveVendorUser(vendorId: string) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (principalId: string) =>
      vendorRegistryApiService.removeVendorUser(vendorId, principalId),
    onSuccess: () => invalidateMembership(queryClient),
  })
}
